import { useState, useMemo } from "react";
import type { AuditLog } from "@/mocks/configuracoes";
import SettingsStatusBadge from "./SettingsStatusBadge";

interface SystemAuditTableProps {
  logs: AuditLog[];
  onViewDetail?: (log: AuditLog) => void;
}

export default function SystemAuditTable({ logs, onViewDetail }: SystemAuditTableProps) {
  const [search, setSearch] = useState("");
  const [filterModule, setFilterModule] = useState("");
  const [filterAction, setFilterAction] = useState("");

  const modules = useMemo(() => {
    const set = new Set(logs.map((l) => l.module));
    return Array.from(set).sort();
  }, [logs]);

  const actions = useMemo(() => {
    const set = new Set(logs.map((l) => l.action));
    return Array.from(set).sort();
  }, [logs]);

  const filtered = useMemo(() => {
    return logs.filter((l) => {
      if (search) {
        const q = search.toLowerCase();
        if (!l.user.toLowerCase().includes(q) && !l.entity.toLowerCase().includes(q) && !l.description.toLowerCase().includes(q)) return false;
      }
      if (filterModule && l.module !== filterModule) return false;
      if (filterAction && l.action !== filterAction) return false;
      return true;
    });
  }, [logs, search, filterModule, filterAction]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input type="text" placeholder="Buscar por usuário, entidade ou descrição..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none" />
        </div>
        <select value={filterModule} onChange={(e) => setFilterModule(e.target.value)} className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
          <option value="">Todos os módulos</option>
          {modules.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)} className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
          <option value="">Todas as ações</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <div className="text-xs text-foreground-500">{filtered.length} de {logs.length} registros</div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-background-200">
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Data/Hora</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Usuário</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Ação</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Módulo</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Entidade</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Descrição</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Status</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                <td className="py-2.5 px-3 text-foreground-500 text-xs font-mono whitespace-nowrap">{l.datetime}</td>
                <td className="py-2.5 px-3 text-foreground-700 text-xs">{l.user}</td>
                <td className="py-2.5 px-3 text-foreground-600 text-xs capitalize">{l.action}</td>
                <td className="py-2.5 px-3 text-foreground-500 text-xs">{l.module}</td>
                <td className="py-2.5 px-3 text-foreground-700 text-xs max-w-[160px] truncate">{l.entity}</td>
                <td className="py-2.5 px-3 text-foreground-500 text-xs max-w-[200px] truncate">{l.description}</td>
                <td className="py-2.5 px-3"><SettingsStatusBadge status={l.status} /></td>
                <td className="py-2.5 px-3">
                  <button onClick={() => onViewDetail?.(l)} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-primary-600 transition-colors" title="Ver detalhes">
                    <i className="ri-eye-line text-sm" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-10 text-foreground-400">
          <i className="ri-history-line text-3xl mb-2 block" />
          <p className="text-sm">Nenhum registro de auditoria encontrado.</p>
        </div>
      )}
    </div>
  );
}